import { useEffect, useState } from 'react';

import { GetServerSidePropsContext, NextPage } from 'next';
import { useRouter } from 'next/router';

import Banner from 'src/components/celebs/celebsDetails/banner';
import Filmography from 'src/components/celebs/celebsDetails/filmography';
import SEOMeta from 'src/components/pageMeta/SEOMeta';
import { SeoMetaObject } from 'src/components/pageMeta/SEOMetaTypes';
import {
  getBreadCrumbs,
  getPersonSchema,
  getSeoData,
  getSeoUrl,
  replaceMetaData
} from 'src/components/pageMeta/SEOMetaUtils';
import { ContentTypes } from 'src/components/thumbnail/types';
import { useAppStore } from 'src/hooks/useAppStore';
import { getCelebsResult } from 'src/services/apiRequests/CelebsService';
import {
  ActorInfo,
  CelebsMetaData
} from 'src/services/responseTypes/celebsResponse';
import SimpleStore from 'src/store/SimpleStore';
import { Nullable } from 'src/types/consumption';
import {
  CelebRedirectStatus,
  celebProfileFeatureFlag,
  fetchCelebFilmographyData,
  getCelebName,
  redirectHandler
} from 'src/utils/celebsUtils/celebsUtils';
import { DynamicObject } from 'src/utils/commonTypes';
import { DISPLAY_LANGUAGE_COOKIE } from 'src/utils/constants/cookieConstants';
import { HTTP_URL_PARAMS } from 'src/utils/constants/HttpMethodsConstants';
import {
  CountryCode,
  DefaultContentLanguages
} from 'src/utils/constants/stringConstants';

import {
  INITIAL_LAUNCH,
  USER_LOCATION_DATA
} from 'src/utils/constants/utilConstants';
import * as styledComponents from '../../../../components/celebs/celebsDetails/celebStyle';
type FilmographyPageProps = {
  celebrityDetails: ActorInfo;
  filmographyData: CelebsMetaData;
  seoMetaAssets: any;
  seoMetaObject?: SeoMetaObject;
  pageDetails: {
    contentType: ContentTypes;
  };
};

const CelebsFilmography: NextPage<FilmographyPageProps> = ({
  celebrityDetails,
  filmographyData,
  seoMetaAssets: seoMetaAssetsFromServer,
  seoMetaObject,
  pageDetails
}) => {
  const router = useRouter();
  const { remoteConfig, initialLaunchData, appConfig } = useAppStore();
  const celebQuery = router.query.celebName;

  useEffect(() => {
    const redirectStatus = celebProfileFeatureFlag(
      remoteConfig,
      celebrityDetails,
      true
    );
    redirectHandler(
      redirectStatus as CelebRedirectStatus,
      router,
      celebQuery as string
    );
  }, []);

  const currentYear = new Date().getFullYear().toString();

  const [seoMetaAssetsClient, setSeoMetaAssetsClient] =
    useState<Nullable<DynamicObject>>(null);

  const seoMetaAssets = seoMetaAssetsFromServer ?? seoMetaAssetsClient;
  const countryCode = SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE);
  const displayLanguage = SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE);

  const allDisplayLanguages = initialLaunchData?.getlang?.display || [];

  useEffect(() => {
    (async () => {
      const assets = await getSeoData(
        countryCode,
        displayLanguage,
        router.asPath
      );
      setSeoMetaAssetsClient(assets);
    })();
  }, []);

  const contentType = 'filmography';
  let celebName = '';
  if (celebrityDetails) {
    celebName = getCelebName(celebrityDetails?.name);
  } else {
    const celebData = celebQuery as string;
    celebName = getCelebName(celebData);
  }

  const seoUrl = getSeoUrl(contentType as string, router.asPath, countryCode);

  const staticMetaData =
    seoUrl && seoMetaAssets && Object.keys(seoMetaAssets).length
      ? seoMetaAssets[seoUrl]
      : null;
  const metaDataType = {
    /** If content is latest or latest1 title will read from only seo_meta.json file */
    title: staticMetaData?.title || '',
    description: staticMetaData?.description || ''
  };

  const replaceFromArray = ['<Celebrity-Name>', '<Current Year>'];

  const replaceToArray = [celebName, currentYear];

  const { appConfig: appConfigFromServer } = useAppStore();

  const isRequestedByBot =
    (appConfig.isRequestedByBot as boolean) ||
    (appConfigFromServer.isRequestedByBot as boolean);

  const metaData = replaceMetaData({
    metaDataObj: metaDataType,
    languages: allDisplayLanguages,
    languageCode: displayLanguage,
    replaceFromArray: replaceFromArray,
    replaceToArray: replaceToArray
  });

  const seoMetaProps = {
    isConsumption: false,
    pageMeta: metaData,
    tableSchema: true,
    header: celebName ? `Top ${celebName} Movie Details:` : '',
    seoMetaObj: seoMetaObject,
    itemListSchema: filmographyData,
    breadcrumbs: getBreadCrumbs({
      pageDetails,
      router,
      contentTitle: celebName
    }),
    person: getPersonSchema({
      celebrityDetails,
      router
    })
  };

  const renderTitle = () => {
    return <div css={styledComponents.commonTitleStyles}>{celebName}</div>;
  };

  const renderContentTitle = () => {
    return (
      <h1
        data-testid="castContent"
        css={styledComponents.CastContent}>{`${celebName} Movies`}</h1>
    );
  };

  return (
    <>
      <SEOMeta {...seoMetaProps} />
      <styledComponents.celebContainer>
        {celebrityDetails && (
          <Banner celebData={celebrityDetails} renderTitle={renderTitle} />
        )}
        <Filmography
          celebData={celebrityDetails}
          renderContentTitle={renderContentTitle}
        />
      </styledComponents.celebContainer>
    </>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const countryCode = SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE);
  const displayLanguage = SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE);
  const contentLanugage = SimpleStore.get(HTTP_URL_PARAMS.CONTENT_LANGUAGE);
  const initialLaunchData = SimpleStore.get(INITIAL_LAUNCH);
  const usersLocationData = SimpleStore.get(USER_LOCATION_DATA);
  const { IN } = CountryCode;
  const pathName = decodeURI(context.req?.url as string);

  const celebQuery = context.query.celebName;
  const reqObj = {
    celebName: celebQuery as string
  };

  const filmographyReqObject = {
    query: celebQuery,
    country: usersLocationData?.country_code || IN,
    limit: 25,
    page: 0,
    filters: {},
    languages: contentLanugage || DefaultContentLanguages,
    translation: displayLanguage || DISPLAY_LANGUAGE_COOKIE
  };

  const pageDetails = {
    contentType: ContentTypes.celebs
  };

  const resolvedDataArray = await Promise.allSettled([
    getCelebsResult(reqObj),
    getSeoData(countryCode, displayLanguage, pathName),
    fetchCelebFilmographyData(
      initialLaunchData?.platform_token?.token,
      filmographyReqObject
    )
  ]);

  const [celebDetails, seoMetaAssets, filmographyData] = resolvedDataArray.map(
    (resolvedData) => {
      if (resolvedData.status === 'fulfilled') {
        return resolvedData.value;
      } else {
        return null;
      }
    }
  );

  const celebrityDetails = celebDetails?.celebrityDetails || null;

  return {
    props: {
      celebrityDetails,
      filmographyData,
      seoMetaAssets,
      pageDetails
    }
  };
};

export default CelebsFilmography;
